import type { BidVoidedFrame, WsClientFrame } from '@neon-one/auctions-types';
import type { OutbidToastPayload } from '../stores/outbidToast';

// The wire payload as it may actually arrive: every field optional so a
// malformed / older producer can't throw here. Shape is NOT redeclared —
// it is derived from the auctions-types frame schema.
type VoidPayload = Partial<BidVoidedFrame['payload']>;

/**
 * GT1-442 — turn one `bid.voided` frame into the shell snackbar payload for
 * whichever role THIS bidder plays in that void, or null when they have no
 * stake in it.
 *
 * A single void action produces one frame, and that one frame carries both
 * identities, so the whole decision is local:
 *   - `bidderUuid` === me                  → my bid was removed
 *   - `recalculatedHighBidderUuid` === me  → the removal promoted me to leader
 *   - neither                              → silent (not my item)
 *
 * The promotion branch deliberately does NOT come off `item.high_bid_changed`:
 * that frame is also emitted on ordinary bid placement, so a bidder would be
 * told "you're now the high bidder" every time they placed a bid themselves.
 *
 * `reason` is admin-authored free text and is never read here — bidder-facing
 * wording is fixed ("Removed", bid-void-design.md §6.1).
 */
export function resolveVoidToast(
  frame: WsClientFrame,
  eventSlug: string | undefined,
  myBidderUuid: string | null | undefined,
): OutbidToastPayload | null {
  if (frame.type !== 'bid.voided') return null;
  // No slug → nowhere to route the tap; no uuid (anonymous browser) → no
  // stake to have. Either way there is nothing to say.
  if (!eventSlug || !myBidderUuid) return null;

  const payload = frame.payload as VoidPayload | undefined;
  if (!payload) return null;

  const itemId = payload.itemId ?? null;
  const bundleId = payload.bundleId ?? null;
  // A bid.voided frame carries itemId OR bundleId (never both) — gating on
  // itemId alone would silently drop every bundle void (the GT1-360 trap).
  if (itemId == null && bundleId == null) return null;
  if (payload.recalculatedHighBidCents == null) return null;

  const variant = payload.bidderUuid === myBidderUuid
    ? 'removed' as const
    : payload.recalculatedHighBidderUuid === myBidderUuid
      ? 'now-winning' as const
      : null;
  // A void on an item this bidder has no stake in must be silent — the frame
  // still reaches them on the event channel so list prices stay fresh.
  if (variant === null) return null;

  return {
    itemId,
    bundleId,
    // The wire frame doesn't carry a title; the snackbar falls back to a
    // generic phrase (same contract as the outbid toast).
    itemTitle: null,
    // Both variants show the post-void high bid: for 'removed' that's the
    // price the bidder would have to beat to bid again; for 'now-winning'
    // it's their own leading bid.
    newHighBidCents: payload.recalculatedHighBidCents,
    eventSlug,
    variant,
  };
}
